"use client";

import React, { useState } from "react";
import { Input } from "../../components/ui/input-field";
import { Plus, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "../../components/ui/dialog";

let directorIdCounter = 0;
const createDirector = () => ({
  id: `director-${++directorIdCounter}`,
  fullName: "",
  email: "",
});

const DIRECTOR_FIELDS = ["fullName", "email"];

function safeSet(obj, key, value) {
  if (typeof key !== "string") return obj;
  const copy = Object.assign({}, obj);
  Object.defineProperty(copy, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
  return copy;
}

function safeGet(obj, key) {
  if (typeof key !== "string") return undefined;
  return Object.prototype.hasOwnProperty.call(obj, key)
    ? Object.getOwnPropertyDescriptor(obj, key)?.value
    : undefined;
}

function hasDuplicateEmails(directors) {
  const emails = directors
    .map((d) => d.email.trim().toLowerCase())
    .filter((e) => e);
  return new Set(emails).size !== emails.length;
}

export default function DirectorsStep({
  formData,
  updateFormData,
  onNext,
  onBack,
}) {
  const [errors, setErrors] = useState({});
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const directors = formData.directors || [];
  const hasDirectors = directors.length > 0;

  const updateDirectors = (newDirectors) => {
    updateFormData({ directors: newDirectors });
  };

  const addDirector = () => {
    updateDirectors([...directors, createDirector()]);
  };

  const removeDirector = (index) => {
    const updated = directors.filter((_, i) => i !== index);
    updateDirectors(updated);
    // Rebuild errors: preserve non-director errors, shift director indices
    let newErrors = {};
    Object.entries(errors).forEach(([key, val]) => {
      const match = key.match(/^directors\.(\d+)\./);
      if (!match) {
        // Preserve non-director errors (e.g. "general")
        newErrors = safeSet(newErrors, key, val);
        return;
      }
      const errIdx = parseInt(match[1], 10);
      if (errIdx < index) {
        newErrors = safeSet(newErrors, key, val);
      } else if (errIdx > index) {
        const shiftedKey = key.replace(
          `directors.${errIdx}.`,
          `directors.${errIdx - 1}.`
        );
        newErrors = safeSet(newErrors, shiftedKey, val);
      }
    });
    setErrors(newErrors);
  };

  const updateDirector = (index, field, value) => {
    if (!DIRECTOR_FIELDS.includes(field)) return;
    const updated = directors.map((d, i) =>
      i === index ? safeSet(d, field, value) : d
    );
    updateDirectors(updated);
    const errorKey = `directors.${index}.${field}`;
    setErrors((prev) => safeSet(prev, errorKey, undefined));
  };

  const validate = () => {
    let newErrors = {};

    if (directors.length === 0) {
      newErrors = safeSet(
        newErrors,
        "general",
        "Please add at least one director"
      );
      setErrors(newErrors);
      return false;
    }

    directors.forEach((director, index) => {
      if (!director.fullName.trim()) {
        newErrors = safeSet(
          newErrors,
          `directors.${index}.fullName`,
          "Director full name is required"
        );
      }

      if (!director.email.trim()) {
        newErrors = safeSet(
          newErrors,
          `directors.${index}.email`,
          "Director email address is required"
        );
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(director.email.trim())) {
        newErrors = safeSet(
          newErrors,
          `directors.${index}.email`,
          "Please enter a valid email address"
        );
      }
    });

    // Check for duplicate emails — iterate over directors directly
    // to ensure error keys match the correct director index
    const seen = new Set();
    directors.forEach((director, index) => {
      const email = director.email.trim().toLowerCase();
      if (!email) return;
      if (seen.has(email)) {
        newErrors = safeSet(
          newErrors,
          `directors.${index}.email`,
          "Duplicate email address"
        );
      }
      seen.add(email);
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const isFormValid =
    directors.length > 0 &&
    !hasDuplicateEmails(directors) &&
    directors.every(
      (d) =>
        d.fullName.trim() &&
        d.email.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())
    );

  return (
    <div className="flex flex-col gap-10 pt-10 pb-16">
      {/* Heading */}
      <div className="flex flex-col gap-6 items-start w-full">
        <h2 className="text-[32px] font-semibold leading-[40px] text-[#33125f] text-center w-full">
          Directors
        </h2>
      </div>

      {/* Business Name Card */}
      <div className="flex flex-col gap-3 w-full">
        <p className="text-[14px] text-[#6D6584] leading-[20px]">You</p>
        <div className="flex items-center gap-3 w-full h-[88px] bg-white border border-[#ebe9ef] rounded-[16px] pl-4 pr-6 py-6">
          <div className="flex items-center justify-center w-[48px] h-[48px] rounded-full border border-black/5 bg-gray-50 shrink-0">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 7H4C3.44772 7 3 7.44772 3 8V19C3 19.5523 3.44772 20 4 20H20C20.5523 20 21 19.5523 21 19V8C21 7.44772 20.5523 7 20 7Z"
                stroke="#6D6584"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 7V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V7"
                stroke="#6D6584"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="text-[20px] font-semibold leading-[24px] text-[#423E51]">
            {formData.businessName || "Business"}
          </p>
        </div>
      </div>

      {/* Empty State */}
      {!hasDirectors && (
        <div className="flex flex-col items-center gap-6 w-full">
          {/* Illustration */}
          <div className="relative w-[194px] h-[136px]">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <svg
                width="104"
                height="104"
                viewBox="0 0 104 104"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="22"
                  y="30"
                  width="60"
                  height="48"
                  rx="4"
                  stroke="#C4A0E8"
                  strokeWidth="2"
                />
                <path
                  d="M42 30V22C42 18.6863 44.6863 16 48 16H56C59.3137 16 62 18.6863 62 22V30"
                  stroke="#C4A0E8"
                  strokeWidth="2"
                />
              </svg>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-[12%] left-[16%] w-[24px] h-[24px]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"
                  fill="#33125f"
                />
              </svg>
            </div>
            <div className="absolute top-[8%] left-[26%] w-[9px] h-[9px] rounded-full bg-[#00FFFF]" />
            <div className="absolute bottom-[1%] left-[30%] w-[51px] h-[51px] rounded-full bg-[#F2E9FB]" />
          </div>

          <p className="text-[20px] font-normal leading-[32px] text-[#423E51] text-center">
            Add directors or team members associated with this business.
          </p>
        </div>
      )}

      {/* Director Forms */}
      {hasDirectors && (
        <div className="flex flex-col gap-8 w-full">
          {directors.map((director, index) => (
            <div
              key={director.id || index}
              className="flex flex-col gap-6 w-full"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-[32px] font-semibold leading-[40px] text-[#33125f]">
                  Director {index + 1}
                </h3>
                {directors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setRemoveConfirm(index)}
                    className="flex items-center gap-2 text-[14px] font-semibold text-red-500 hover:text-red-600 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>

              <Input
                text="Director Full Name"
                placeholder="Enter Director Full Name"
                value={director.fullName}
                onChange={(e) =>
                  updateDirector(index, "fullName", e.target.value)
                }
                state={
                  safeGet(errors, `directors.${index}.fullName`)
                    ? "error"
                    : "default"
                }
                helperText={
                  safeGet(errors, `directors.${index}.fullName`) || false
                }
              />

              <Input
                text="Director Email Address"
                placeholder="Enter Director Email Address"
                value={director.email}
                onChange={(e) => updateDirector(index, "email", e.target.value)}
                state={
                  safeGet(errors, `directors.${index}.email`)
                    ? "error"
                    : "default"
                }
                helperText={
                  safeGet(errors, `directors.${index}.email`) || false
                }
              />
            </div>
          ))}
        </div>
      )}

      {/* Add Director Button */}
      <button
        type="button"
        onClick={addDirector}
        className="flex items-center gap-2 py-4 w-full cursor-pointer group"
      >
        <Plus className="w-4 h-4 text-[#060606] group-hover:text-[#33125f] transition" />
        <span className="text-[16px] font-semibold leading-[24px] text-[#060606] group-hover:text-[#33125f] transition">
          Add Director
        </span>
      </button>

      {/* Error Message */}
      {errors.general && (
        <p className="text-xs text-red-500 text-center">{errors.general}</p>
      )}

      {/* Buttons */}
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="h-[48px] w-[150px] border border-[#ebe9ef] rounded-full text-[14px] font-semibold text-[#6d6584] hover:bg-gray-50 transition"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!isFormValid}
          className={`h-[48px] w-[150px] rounded-full text-[14px] font-semibold transition ${
            isFormValid
              ? "text-white cursor-pointer hover:opacity-90"
              : "bg-[#f2f1f4] text-[#9d96af] cursor-not-allowed"
          }`}
          style={
            isFormValid
              ? {
                  backgroundImage:
                    "linear-gradient(65deg, #57249C 10.66%, #EB3366 113.47%)",
                }
              : undefined
          }
        >
          Next
        </button>
      </div>

      {/* Remove Director Confirmation Modal */}
      <Dialog
        open={removeConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveConfirm(null);
        }}
      >
        <DialogContent className="max-w-md p-0 bg-white rounded-xl border-0 shadow-2xl">
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <DialogTitle className="text-2xl font-semibold text-[#33125f]">
                Remove director?
              </DialogTitle>
              <DialogClose className="border border-[#ebe9ef] rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-5 w-5 text-[#6d6584]" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <div className="space-y-4">
              <DialogDescription className="text-base text-[#6d6584] leading-relaxed">
                {(() => {
                  const director =
                    removeConfirm !== null ? directors.at(removeConfirm) : null;
                  return director
                    ? `Are you sure you want to remove ${director.fullName || `Director ${removeConfirm + 1}`}? This action cannot be undone.`
                    : "Are you sure you want to remove this director? This action cannot be undone.";
                })()}
              </DialogDescription>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setRemoveConfirm(null)}
                  className="flex-1 h-[48px] border border-[#ebe9ef] rounded-full text-[14px] font-semibold text-[#6d6584] hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    removeDirector(removeConfirm);
                    setRemoveConfirm(null);
                  }}
                  className="flex-1 h-[48px] rounded-full text-[14px] font-semibold text-white bg-red-500 hover:bg-red-600 transition"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
